import React, { useState, useEffect } from 'react';
import './AuthGuard.css'; // Reuse AuthGuard styles

function Registration({ onRegistered, onCancel, inviteCode: inviteCodeProp }) {
  const [step, setStep] = useState(inviteCodeProp ? 'register' : 'invite'); // 'invite' or 'register'
  const [inviteCode, setInviteCode] = useState(inviteCodeProp || '');
  const [formData, setFormData] = useState({
    inviteCode: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [inviteCodeValid, setInviteCodeValid] = useState(null); // null, true, false
  const [successMessage, setSuccessMessage] = useState(null);

  // Auto-validate invite code from URL prop
  useEffect(() => {
    if (inviteCodeProp && inviteCodeProp.trim()) {
      const code = inviteCodeProp.toUpperCase().trim();
      setInviteCode(code);
      setFormData(prev => ({ ...prev, inviteCode: code }));
      // Auto-validate the code
      validateInviteCode(code).then(isValid => {
        if (isValid) {
          setStep('register');
        }
      });
    }
  }, [inviteCodeProp]);

  // Validate invite code
  const validateInviteCode = async (code) => {
    if (!code || code.trim() === '') {
      setInviteCodeValid(null);
      return false;
    }

    setIsValidatingCode(true);
    try {
      const response = await fetch(`/api/invite-codes/validate/${encodeURIComponent(code)}`);
      const data = await response.json();
      
      if (response.status === 503) {
        // Database not configured
        setInviteCodeValid(false);
        setErrors(prev => ({ ...prev, inviteCode: data.error || 'Database not configured. Registration requires database setup.' }));
        return false;
      } else if (data.valid) {
        setInviteCodeValid(true);
        setErrors(prev => ({ ...prev, inviteCode: null }));
        return true;
      } else {
        setInviteCodeValid(false);
        setErrors(prev => ({ ...prev, inviteCode: data.error || 'Invalid invite code' }));
        return false;
      }
    } catch (error) {
      console.error('Invite code validation error:', error);
      setInviteCodeValid(false);
      setErrors(prev => ({ ...prev, inviteCode: 'Failed to validate invite code. Please check if the server is running and database is configured.' }));
      return false;
    } finally {
      setIsValidatingCode(false);
    }
  };

  // Handle invite code entry and proceed to registration
  const handleInviteCodeSubmit = async (e) => {
    e.preventDefault();
    setErrors({ inviteCode: null });

    if (!inviteCode.trim()) {
      setErrors({ inviteCode: 'Please enter an invitation code' });
      return;
    }

    const isValid = await validateInviteCode(inviteCode.trim().toUpperCase());
    
    if (isValid) {
      // Save invite code to formData, mark as valid, and proceed to registration step
      const validatedCode = inviteCode.trim().toUpperCase();
      setFormData({ ...formData, inviteCode: validatedCode });
      setInviteCodeValid(true); // Ensure it's marked as valid for registration form
      setStep('register');
    }
  };

  // Handle invite code change (for real-time validation)
  const handleInviteCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setInviteCode(code);
    setInviteCodeValid(null);
    setErrors(prev => ({ ...prev, inviteCode: null }));
    
    // Optional: Debounce validation for user feedback
    if (code.trim().length >= 3) {
      setTimeout(() => validateInviteCode(code), 500);
    }
  };

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Invite code is already validated in step 1, so we skip validation here
    // But we still check it exists
    if (!formData.inviteCode.trim()) {
      newErrors.inviteCode = 'Invite code is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage(null);

    if (!validateForm()) {
      return;
    }

    if (inviteCodeValid !== true) {
      setErrors(prev => ({ ...prev, inviteCode: 'Please validate your invite code first' }));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/users/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username.trim(),
          email: formData.email.trim(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          inviteCode: formData.inviteCode.toUpperCase()
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage('Account created successfully! You can now login.');
        setTimeout(() => {
          if (onRegistered) {
            onRegistered();
          }
        }, 2000);
      } else {
        setErrors({ general: data.error || 'Registration failed. Please try again.' });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'Registration failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-guard-overlay">
      <div className="auth-guard-container">
        <div className="auth-header">
          <h2>🔐 Create Account</h2>
          <p>{step === 'invite' ? 'Invitation required' : 'Complete your registration'}</p>
        </div>

        {successMessage ? (
          <div className="auth-info" style={{ background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724' }}>
            <p>{successMessage}</p>
          </div>
        ) : step === 'invite' ? (
          // Step 1: Invite Code Entry Screen
          <div>
            <div className="auth-info" style={{ 
              background: '#fff3cd', 
              border: '1px solid #ffc107', 
              color: '#856404',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <p style={{ margin: 0, fontSize: '1.1rem', lineHeight: '1.6' }}>
                <strong>⚠️ Limited Access</strong>
              </p>
              <p style={{ margin: '10px 0 0 0', fontSize: '1rem' }}>
                Due to limited access, an access code is necessary to register for an account.
              </p>
              <p style={{ margin: '10px 0 0 0', fontSize: '0.95rem', opacity: 0.9 }}>
                If you have a valid access code, please enter it below to proceed with registration.
              </p>
            </div>

            <form onSubmit={handleInviteCodeSubmit} className="auth-form">
              <div className="auth-input-group">
                <label htmlFor="inviteCode">Access Code: <span style={{color: 'red'}}>*</span></label>
                <input
                  id="inviteCode"
                  name="inviteCode"
                  type="text"
                  value={inviteCode}
                  onChange={handleInviteCodeChange}
                  placeholder="Enter your access code"
                  className="auth-input"
                  style={{ textTransform: 'uppercase', textAlign: 'center', fontSize: '1.1rem', letterSpacing: '2px' }}
                  required
                  disabled={isValidatingCode}
                  autoFocus
                />
                {isValidatingCode && (
                  <small style={{ color: '#667eea', display: 'block', marginTop: '8px' }}>Validating...</small>
                )}
                {inviteCodeValid === true && (
                  <small style={{ color: '#28a745', display: 'block', marginTop: '8px' }}>✓ Valid invitation code</small>
                )}
                {errors.inviteCode && (
                  <div className="auth-error-message" style={{ marginTop: '10px' }}>
                    ❌ {errors.inviteCode.replace(/invite code/gi, 'access code').replace(/Invite code/gi, 'Access code')}
                  </div>
                )}
              </div>

              <button 
                type="submit" 
                className="auth-submit-btn" 
                disabled={isValidatingCode || !inviteCode.trim()}
                style={{ marginTop: '20px' }}
              >
                {isValidatingCode ? 'Validating...' : 'Continue to Registration'}
              </button>
            </form>

            <div className="auth-actions" style={{ marginTop: '20px', textAlign: 'center' }}>
              <button onClick={onCancel} className="auth-link-btn">
                Back to Main Page
              </button>
            </div>
          </div>
        ) : (
          // Step 2: Registration Form (only shown after valid invite code)
          <form onSubmit={handleSubmit} className="auth-form">
            <div style={{ 
              background: '#d1ecf1', 
              border: '1px solid #bee5eb', 
              color: '#0c5460',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem'
            }}>
              <strong>Access Code Verified:</strong> {formData.inviteCode}
            </div>

            {/* Username */}
            <div className="auth-input-group">
              <label htmlFor="username">Username: <span style={{color: 'red'}}>*</span></label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                placeholder="Enter your username"
                className="auth-input"
                required
                disabled={isSubmitting}
              />
              {errors.username && (
                <div className="auth-error-message" style={{ marginTop: '5px' }}>
                  ❌ {errors.username}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="auth-input-group">
              <label htmlFor="email">Email: <span style={{color: 'red'}}>*</span></label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                className="auth-input"
                required
                disabled={isSubmitting}
              />
              {errors.email && (
                <div className="auth-error-message" style={{ marginTop: '5px' }}>
                  ❌ {errors.email}
                </div>
              )}
            </div>

            {/* Password */}
            <div className="auth-input-group">
              <label htmlFor="password">Password: <span style={{color: 'red'}}>*</span></label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password (min 6 characters)"
                className="auth-input"
                required
                disabled={isSubmitting}
              />
              {errors.password && (
                <div className="auth-error-message" style={{ marginTop: '5px' }}>
                  ❌ {errors.password}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="auth-input-group">
              <label htmlFor="confirmPassword">Confirm Password: <span style={{color: 'red'}}>*</span></label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="auth-input"
                required
                disabled={isSubmitting}
              />
              {errors.confirmPassword && (
                <div className="auth-error-message" style={{ marginTop: '5px' }}>
                  ❌ {errors.confirmPassword}
                </div>
              )}
            </div>

            {/* First Name */}
            <div className="auth-input-group">
              <label htmlFor="firstName">First Name: <span style={{color: 'red'}}>*</span></label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                className="auth-input"
                required
                disabled={isSubmitting}
              />
              {errors.firstName && (
                <div className="auth-error-message" style={{ marginTop: '5px' }}>
                  ❌ {errors.firstName}
                </div>
              )}
            </div>

            {/* Last Name */}
            <div className="auth-input-group">
              <label htmlFor="lastName">Last Name: <span style={{color: 'red'}}>*</span></label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                className="auth-input"
                required
                disabled={isSubmitting}
              />
              {errors.lastName && (
                <div className="auth-error-message" style={{ marginTop: '5px' }}>
                  ❌ {errors.lastName}
                </div>
              )}
            </div>

            {errors.general && (
              <div className="auth-error-message">
                ❌ {errors.general}
              </div>
            )}

            <button 
              type="submit" 
              className="auth-submit-btn" 
              disabled={isSubmitting || inviteCodeValid !== true}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        )}

            {step === 'register' && (
              <div className="auth-actions" style={{ marginTop: '20px' }}>
                <button 
                  onClick={() => {
                    setStep('invite');
                    setInviteCode('');
                    setErrors({});
                  }} 
                  className="auth-link-btn"
                  type="button"
                >
                  ← Back to Invitation Code
                </button>
              </div>
            )}

        <div className="auth-footer">
          <p className="auth-security-note">
            🔐 Registration requires a valid access code
          </p>
        </div>
      </div>
    </div>
  );
}

export default Registration;
